import {
  Component,
  inject,
  signal,
  computed,
  OnInit,
  QueryList,
  ViewChildren,
  ElementRef,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { AvatarModule } from 'primeng/avatar';
import {
  PartnerService,
  PartnerRequest,
  UpdatePartnerRequest,
} from '../../services/partner';
import { DropdownService, DropdownItem } from '../../services/dropdown';
import { ToastService } from '../../services/toast';
import { AuthService } from '../../services/auth';

type StatusFilter = 'all' | 'verified' | 'pending';

@Component({
  selector: 'app-partners',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    TooltipModule,
    InputTextModule,
    PasswordModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    TableModule,
    AvatarModule,
  ],
  templateUrl: './partners.html',
  styleUrls: ['./partners.css'],
})
export class Partners implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);

  /** The add/edit form now lives in a dialog, matching the clients screen. */
  readonly showModal = signal(false);
  private svc = inject(PartnerService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  private auth = inject(AuthService);
  private platformId = inject(PLATFORM_ID);

  // ---- list + filter ----
  partners = signal<any[]>([]);
  listLoading = signal(false);
  search = signal('');
  statusFilter = signal<StatusFilter>('all');

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    const st = this.statusFilter();
    return this.partners().filter((p) => {
      const matchesText =
        !q ||
        (p.partnerName || '').toLowerCase().includes(q) ||
        (p.partnerCode || '').toLowerCase().includes(q);
      const matchesStatus =
        st === 'all' || (st === 'verified' ? !!p.kycVerified : !p.kycVerified);
      return matchesText && matchesStatus;
    });
  });

  // ---- form (add / edit) ----
  saving = signal(false);
  editing = signal(false);
  editId = signal<number | null>(null);

  // Preview / actual partner code shown read-only on the form.
  partnerCode = signal('');
  codeLoading = signal(false);

  roleOptions = signal<DropdownItem[]>([]);
  selectedRoles = signal<Set<number>>(new Set());

  // ---- verify (OTP) ----
  verifying = signal(false);
  sendingOtp = signal(false);
  showOtpModal = signal(false);
  partnerId = signal<number | null>(null);
  partnerLabel = signal('');
  otpIdentifier = signal(''); // mobile/username the OTP is sent to (for resend)

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  form: FormGroup = this.fb.group({
    partnerName: ['', Validators.required],
    contactPerson: [''],
    email: ['', Validators.email], // optional per spec; validated only if filled
    phone: ['', [Validators.required, Validators.pattern(/^\d{6,15}$/)]],
    address: [''],
    city: [''],
    state: [''],
    pincode: [''],
    username: ['', Validators.required],
    password: ['', [Validators.required, Validators.minLength(6)]],
    // Where OTP / password are shared (PartnerMessageType). Defaults: SMS + Email on.
    isWhatsApp: [false],
    isSMS: [true],
    isEmail: [true],
  });

  /** At least one delivery channel must be selected. */
  get noChannel(): boolean {
    const v = this.form.value;
    return !v.isWhatsApp && !v.isSMS && !v.isEmail;
  }

  otpForm: FormGroup = this.fb.group({
    otp: this.fb.array(
      Array.from({ length: 6 }, () =>
        this.fb.control('', { nonNullable: true, validators: Validators.required }),
      ),
    ),
  });

  get otpArray(): FormArray<FormControl<string>> {
    return this.otpForm.get('otp') as FormArray<FormControl<string>>;
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.dropdown.roles().subscribe((o) => this.roleOptions.set(o));
    this.load();
    this.startAdd();
  }

  // ===================== list =====================
  load() {
    this.listLoading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.listLoading.set(false);
        const data = res?.data ?? res ?? [];
        this.partners.set(Array.isArray(data) ? data : []);
      },
      error: () => this.listLoading.set(false),
    });
  }

  // ===================== roles =====================
  isRoleSelected(id: number) {
    return this.selectedRoles().has(id);
  }

  toggleRole(id: number) {
    const set = new Set(this.selectedRoles());
    set.has(id) ? set.delete(id) : set.add(id);
    this.selectedRoles.set(set);
  }

  // ===================== add / edit mode =====================
  startAdd() {
    this.editing.set(false);
    this.editId.set(null);
    this.form.reset({
      partnerName: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      username: '',
      password: '',
      isWhatsApp: false,
      isSMS: true,
      isEmail: true,
    });
    // Login fields are part of creation only.
    this.form.get('username')?.enable();
    this.form.get('password')?.enable();
    this.selectedRoles.set(new Set([2])); // preselect default role id 2
    this.loadPreviewCode();
  }

  loadPreviewCode() {
    this.codeLoading.set(true);
    this.partnerCode.set('');
    this.svc.previewPartnerCode().subscribe({
      next: (res: any) => {
        this.codeLoading.set(false);
        this.partnerCode.set(res?.data?.previewPartnerCode ?? res?.previewPartnerCode ?? '');
      },
      error: () => this.codeLoading.set(false),
    });
  }

  /** Open the partner's own page — gateway, products, messaging and live/test live there. */
  openPartner(p: any) {
    this.router.navigate(['/partners', p.id]);
  }

  /** Avatar initials for the list, same treatment as the clients table. */
  initialsOf(name?: string | null): string {
    const n = (name || '').trim();
    if (!n) return 'P';
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'P';
  }

  /** "Add Partner" — reset to a blank form and show the dialog. */
  openAdd() {
    this.startAdd();
    this.showModal.set(true);
  }

  startEdit(p: any) {
    this.showModal.set(true);
    this.editing.set(true);
    this.editId.set(p.id);
    this.partnerCode.set(p.partnerCode ?? '');
    this.form.reset({
      partnerName: p.partnerName ?? '',
      contactPerson: p.contactPerson ?? '',
      email: p.email ?? '',
      phone: p.phone ?? '',
      address: p.address ?? '',
      city: p.city ?? '',
      state: p.state ?? '',
      pincode: p.pincode ?? '',
      username: p.username ?? '',
      password: '',
    });
    // username / password are not editable here — disable so they don't block validity or submit.
    this.form.get('username')?.disable();
    this.form.get('password')?.disable();
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Please fill in all required fields');
      return;
    }
    if (!this.editing() && this.noChannel) {
      this.toast.warning('Select at least one channel to share OTP / password');
      return;
    }
    this.editing() ? this.update() : this.add();
  }

  private add() {
    this.saving.set(true);
    const v = this.form.value;
    const payload: PartnerRequest = {
      partnerName: v.partnerName,
      contactPerson: v.contactPerson,
      email: v.email,
      phone: v.phone,
      address: v.address,
      city: v.city,
      state: v.state,
      pincode: v.pincode,
      username: v.username,
      password: v.password,
      roleIds: Array.from(this.selectedRoles()),
      isWhatsApp: !!v.isWhatsApp,
      isSMS: !!v.isSMS,
      isEmail: !!v.isEmail,
    };

    this.svc.addPartner(payload).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to add partner');
          return;
        }
        const code = res?.data?.partnerCode ?? this.partnerCode();
        const id = res?.data?.partnerId ?? res?.data?.id;
        this.toast.success(`Partner created — ${code}`);
        this.showModal.set(false);
        this.load();
        // Immediately fire an OTP to the new partner's mobile for KYC verification.
        if (id != null) {
          this.partnerId.set(Number(id));
          this.partnerLabel.set(v.partnerName);
          this.otpIdentifier.set(String(v.phone ?? v.username ?? ''));
          this.sendOtp(true);
        }
        this.startAdd();
      },
      error: () => this.saving.set(false),
    });
  }

  private update() {
    const id = this.editId();
    if (id == null) return;
    this.saving.set(true);
    const v = this.form.getRawValue();
    const payload: UpdatePartnerRequest = {
      id,
      partnerName: v.partnerName,
      contactPerson: v.contactPerson,
      email: v.email,
      phone: v.phone,
      address: v.address,
      city: v.city,
      state: v.state,
      pincode: v.pincode,
    };

    this.svc.updatePartner(payload).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to update partner');
          return;
        }
        this.toast.success('Partner updated');
        this.showModal.set(false);
        this.load();
        this.startAdd();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== verify (OTP) =====================
  /** Clicking Verify fires an OTP to the partner's mobile (Login OTP flow), then opens the dialog. */
  openVerify(p: any) {
    const identifier = p.phone ?? p.username ?? p.email;
    if (!identifier) {
      this.toast.warning('No mobile number on this partner to send an OTP.');
      return;
    }
    this.partnerId.set(p.id);
    this.partnerLabel.set(p.partnerName ?? p.partnerCode ?? 'this partner');
    this.otpIdentifier.set(String(identifier));
    this.sendOtp(true);
  }

  /** Send (or resend) the OTP to otpIdentifier. Opens the dialog on the first send. */
  sendOtp(openOnSuccess = false) {
    const identifier = this.otpIdentifier();
    if (!identifier) return;
    this.sendingOtp.set(true);
    this.auth.sendOtp(identifier).subscribe({
      next: (res: any) => {
        this.sendingOtp.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to send OTP');
          return;
        }
        const sentTo = res?.data?.otp_sent_to;
        this.toast.success(sentTo ? `OTP sent to ******${String(sentTo).slice(-4)}` : 'OTP sent');
        if (openOnSuccess) this.openOtpModal();
        else {
          this.otpArray.reset();
          this.otpInputs.get(0)?.nativeElement.focus();
        }
      },
      error: () => this.sendingOtp.set(false),
    });
  }

  private openOtpModal() {
    this.otpArray.reset();
    this.showOtpModal.set(true);
    setTimeout(() => this.otpInputs.get(0)?.nativeElement.focus());
  }

  closeOtpModal() {
    this.showOtpModal.set(false);
  }

  onOtpShow() {
    this.otpInputs.get(0)?.nativeElement.focus();
  }

  onInput(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    const digit = target.value.replace(/\D/g, '').slice(0, 1);
    this.otpArray.at(index).setValue(digit);
    target.value = digit;
    if (digit && index < 5) this.otpInputs.get(index + 1)?.nativeElement.focus();
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpArray.at(index).value && index > 0) {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (pasted.length === 6) {
      pasted.split('').forEach((val, i) => this.otpArray.at(i).setValue(val));
      this.otpInputs.get(5)?.nativeElement.focus();
    }
  }

  verifyPartner() {
    if (this.otpForm.invalid) {
      this.toast.warning('Please enter the complete 6-digit OTP');
      return;
    }
    const id = this.partnerId();
    if (id == null) return;

    this.verifying.set(true);
    const otp = Number(this.otpArray.value.join(''));

    this.svc.verifyPartner({ partnerId: id, otp }).subscribe({
      next: (res: any) => {
        this.verifying.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Invalid or expired OTP');
          return;
        }
        this.toast.success('Partner verified successfully!');
        this.closeOtpModal();
        this.partnerId.set(null);
        this.load();
      },
      error: () => this.verifying.set(false),
    });
  }
}
